import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Users, UserPlus, Inbox } from "lucide-react";
import { toast } from "sonner";
import DocumentTitle from "@/components/DocumentTitle";
import { useAuth } from "@/components/AuthProvider";
import { client } from "@/lib/api";
import SignInPrompt from "@/pages/member/SignInPrompt";

/**
 * Wave 15 Step 8 (Vania 3 Oct 2026) · Unified invites inbox.
 *
 * Route: /app/invites
 *
 * Lists everything from GET /api/invites — event attendee / co-organiser
 * invites, squad invites, and friend requests — with inline accept + decline.
 * Pending invites expire after 14 days (filtered server-side).
 */
const META = "var(--ww-text-muted)";
const LINE = "var(--ww-line)";
const CARD = "var(--ww-surface)";
const YELLOW = "#FFD60A";

export default function InvitesPage() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [invites, setInvites] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    client.get("/invites").then((r) => setInvites(r.data.invites || []))
      .catch(() => setInvites([]));
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (!authLoading && !user) {
    return <SignInPrompt title="invites"
                         line="sign in to see who's inviting you."
                         next="/app/invites" testId="invites-signin" />;
  }

  const act = async (inv, verb) => {
    setBusyId(inv.id + verb);
    try {
      const path = routeFor(inv, verb);
      if (!path) throw new Error("unknown_invite_kind");
      await client.post(path);
      toast.success(verb === "accept" ? "accepted" : "declined");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d.replace(/_/g, " ").replace(/\buser\b/g, "member") : "could not " + verb);
    } finally { setBusyId(null); }
  };

  return (
    <div className="flex h-full flex-col" data-testid="invites-page"
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      <DocumentTitle title="invites · watu wako" />
      <header className="flex items-center gap-3 px-4 py-3"
              style={{ borderBottom: `1px solid ${LINE}` }}>
        <button onClick={() => nav("/app")} aria-label="back"
                data-testid="invites-back"
                style={{ background: "transparent", border: 0, color: "var(--ww-text)",
                         cursor: "pointer", display: "flex", alignItems: "center" }}>
          <ArrowLeft size={20} />
        </button>
        <div style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 16,
                       textTransform: "lowercase" }}>invites</div>
      </header>

      <div className="flex-1 overflow-y-auto px-5 pt-4 pb-10" style={{ overscrollBehavior: "contain" }}>
        <div className="mx-auto" style={{ maxWidth: 640 }}>
          {invites === null && (
            <p style={{ fontFamily: "Arial", fontSize: 14, color: META }}>loading…</p>
          )}
          {invites && invites.length === 0 && (
            <div className="rounded-2xl border p-8 text-center" data-testid="invites-empty"
                 style={{ borderColor: LINE, background: CARD }}>
              <span style={{ width: 56, height: 56, borderRadius: 999,
                             background: "var(--ww-ground)",
                             border: `1px solid ${LINE}`, display: "inline-grid",
                             placeItems: "center", color: META }}>
                <Inbox size={22} />
              </span>
              <p className="mt-3" style={{ fontFamily: "Arial", fontSize: 14, color: META }}>
                no pending invites. nothing to accept or decline right now.
              </p>
            </div>
          )}
          {invites && invites.length > 0 && (
            <ul data-testid="invites-list">
              {invites.map((inv) => (
                <InviteRow key={inv.id} inv={inv} busyId={busyId} act={act} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function routeFor(inv, verb) {
  const k = inv.kind_display;
  if (k === "event_attendee" || k === "event_co_organiser") return `/invites/event/${inv.id}/${verb}`;
  if (k === "squad") return `/squads/invites/${inv.id}/${verb}`;
  if (k === "friend") return `/friends/${inv.id}/${verb}`;
  return null;
}

function InviteRow({ inv, busyId, act }) {
  const k = inv.kind_display;
  const inviter = inv.inviter || inv.requester;
  const inviterLabel = inviter?.display_name || inviter?.name || inviter?.handle || "someone";
  const sub = (() => {
    if (k === "event_attendee") return `${inviterLabel} invited you to a private event`;
    if (k === "event_co_organiser") return `${inviterLabel} invited you to co-organise`;
    if (k === "squad") return `${inviterLabel} invited you to the squad`;
    if (k === "friend") return `${inviterLabel} wants to be friends`;
    return "pending invite";
  })();
  const title = (() => {
    if (k === "event_attendee" || k === "event_co_organiser") return (inv.event?.title || "event").toLowerCase();
    if (k === "squad") return (inv.squad?.name || "squad").toLowerCase();
    if (k === "friend") return "@" + (inviter?.handle || "someone").toLowerCase();
    return "·";
  })();
  const Icon = k === "squad" ? Users : k === "friend" ? UserPlus : Calendar;

  return (
    <li data-testid={`invite-${k}-${inv.id}`}
        style={{ padding: "14px 4px", borderBottom: `1px solid ${LINE}`,
                  display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ width: 44, height: 44, borderRadius: 999, background: "var(--ww-sunken)",
                     border: `1px solid ${LINE}`, display: "grid", placeItems: "center" }}>
        <Icon size={20} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 15,
                       textTransform: "lowercase", overflow: "hidden",
                       textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {title}
        </div>
        <div style={{ fontFamily: "Arial", fontSize: 13, color: META,
                       overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {sub.toLowerCase()}
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={() => act(inv, "accept")} disabled={busyId}
                data-testid={`invite-accept-${inv.id}`}
                style={{ padding: "8px 14px", borderRadius: 999, background: YELLOW,
                         color: "#1A1A30", border: 0, fontFamily: "Poppins",
                         fontWeight: 700, fontSize: 12, textTransform: "lowercase",
                         cursor: busyId ? "wait" : "pointer" }}>
          accept
        </button>
        <button onClick={() => act(inv, "decline")} disabled={busyId}
                data-testid={`invite-decline-${inv.id}`}
                style={{ padding: "8px 14px", borderRadius: 999, background: "transparent",
                         color: "var(--ww-text)", border: `1px solid ${LINE}`,
                         fontFamily: "Poppins", fontWeight: 700, fontSize: 12,
                         textTransform: "lowercase",
                         cursor: busyId ? "wait" : "pointer" }}>
          decline
        </button>
      </div>
    </li>
  );
}
